/**
 * The create dialog's optional "fill from a file" field (F-144): pick a
 * storyboard or script, and the agent fills the new board from it on open.
 * The file is read here (as text) so the dialog can refuse the wrong kind
 * before Create, and nothing is uploaded — it rides to the board in memory
 * (`pending-import.ts`).
 *
 * F-153: a host with its own folders (the desktop app's Local Files) adds a
 * second source beside the file dialog — `HostFileBrowser`, inline. For a
 * Short it offers only the files the team marked Selected there.
 */
import type { AgentAttachment, BoardKind } from "@kola/shared";
import { useRef, useState } from "react";

import { hostFileSource } from "../../core/host.js";
import { showToast } from "../../ui/toast.js";
import { AGENT_ATTACHMENT_ACCEPT, attachmentProblem, readAgentAttachment } from "./attachments.js";
import { HostFileBrowser } from "./HostFileBrowser.js";

export interface StoryboardFilePickerProps {
  attachment: AgentAttachment | null;
  /**
   * `hostPath` is set when the file came from the host's folders (F-153) —
   * where it sits, which a Short keeps on its first row (F-155).
   */
  onChange: (attachment: AgentAttachment | null, hostPath?: string) => void;
  /** F-154: a Short's import is the whole job — its hint promises no steps. */
  kind?: BoardKind;
}

export function StoryboardFilePicker({ attachment, onChange, kind = "long" }: StoryboardFilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const source = hostFileSource();
  const [browsing, setBrowsing] = useState(false);

  const pick = async (file: File) => {
    const outcome = await readAgentAttachment(file, "f1");
    if (outcome.ok) {
      setBrowsing(false);
      onChange(outcome.attachment);
    } else showToast(attachmentProblem(outcome.reason, file.name));
  };

  return (
    <div className="create-board-field">
      <span className="create-board-label">
        Fill from a file <span className="create-board-optional">optional</span>
      </span>
      <div className="create-board-file">
        {attachment === null ? (
          <>
            <button type="button" className="btn" onClick={() => inputRef.current?.click()}>
              Choose a storyboard or script…
            </button>
            {source !== null && (
              <button
                type="button"
                className="btn"
                aria-expanded={browsing}
                onClick={() => setBrowsing((open) => !open)}
              >
                From {source.label}…
              </button>
            )}
          </>
        ) : (
          <span className="agent-chip" title={`${attachment.text.length} characters`}>
            {attachment.name}
            <button
              type="button"
              className="agent-chip-remove"
              aria-label={`Remove ${attachment.name}`}
              data-tip="Remove"
              onClick={() => onChange(null)}
            >
              ×
            </button>
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={AGENT_ATTACHMENT_ACCEPT}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void pick(file);
            event.target.value = "";
          }}
        />
      </div>
      {source !== null && browsing && attachment === null && (
        <HostFileBrowser
          source={source}
          onlySelected={kind === "shorts"}
          onPick={(picked, path) => {
            setBrowsing(false);
            onChange(picked, path);
          }}
          onClose={() => setBrowsing(false)}
        />
      )}
      <p className="create-board-file-hint">
        {kind === "shorts"
          ? "The Short is named after it and filled from it, as it is, when it opens — original, English, " +
            "and visual and sound direction (.md, .csv, .tsv, .txt)." +
            (source !== null
              ? ` One from ${source.label} also puts its path on row 1 and a Place video button on row 2.`
              : "")
          : "The board is named after it and filled from it, as it is, when it opens (.md, .csv, .tsv, " +
            ".txt). Splitting, translating and aligning then wait as steps in the Agent panel."}
      </p>
    </div>
  );
}
